import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Typography,
  Input,
  Button,
  Form,
  Calendar,
  Select,
  Spin,
  Result,
  Checkbox,
  Popover,
} from "antd";
import { useNavigate, useParams, useSearchParams, NavLink } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { getMethod, postMethod, putMethod } from "../../services/HttpServices";
import { publicBooking } from "../../constant/Constant";
import {
  Pencil,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Clock,
  Calendar as LucideCalendar,
  User,
  Globe,
  Video,
  CheckCircle2,
  MapPin,
  Sparkles,
  Info,
} from "lucide-react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import MeetingSuccessState from "./MeetingSuccessState";
import LogoSvg from "../../assets/svg/logos/logo-black.svg";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);

const { Title, Text } = Typography;
const { TextArea } = Input;
const fallbackTimezone = dayjs.tz.guess();
const systemTimezone = dayjs.tz.guess();

const isUtcDateTimeString = (value) =>
  typeof value === "string" && /T\d{2}:\d{2}(:\d{2})?(\.\d+)?Z$/i.test(value);

const getUtcSlotStart = (slot, selectedDateValue) => {
  if (!slot) return null;

  if (isUtcDateTimeString(slot)) {
    const parsedUtcSlot = dayjs.utc(slot);
    return parsedUtcSlot.isValid() ? parsedUtcSlot : null;
  }

  if (!selectedDateValue) return null;

  const normalizedSlot = slot.length === 5 ? `${slot}:00` : slot;
  const sourceDate = selectedDateValue.format("YYYY-MM-DD");
  const parsedUtcSlot = dayjs.utc(`${sourceDate}T${normalizedSlot}`);

  return parsedUtcSlot.isValid() ? parsedUtcSlot : null;
};

const formatTimeSlotRange = (timeSlot, duration) => {
  if (!timeSlot) return "-";
  if (isUtcDateTimeString(timeSlot)) {
    const localStart = dayjs.utc(timeSlot).tz(systemTimezone);
    if (!localStart.isValid()) return timeSlot;
    return `${localStart.format("hh:mm A")} - ${localStart.add(duration || 30, "minute").format("hh:mm A")}`;
  }
  // The API returns 'timeSlot' like '10:00:00' or '10:00'. We parse it.
  const parsedSlot =
    timeSlot.includes(":") && timeSlot.length === 5
      ? `${timeSlot}:00`
      : timeSlot;
  const start = dayjs(`2024-01-01T${parsedSlot}`);
  if (!start.isValid()) {
    // legacy format?
    const fallbackStart = dayjs(timeSlot, "hh:mm A");
    if (!fallbackStart.isValid()) return timeSlot;
    return `${fallbackStart.format("hh:mm A")} - ${fallbackStart.add(duration, "minute").format("hh:mm A")}`;
  }

  return `${start.format("hh:mm A")} - ${start.add(duration, "minute").format("hh:mm A")}`;
};

const formatUtcDateTimeInSystemTimezone = (value, format) => {
  if (!value) return "";
  return dayjs.utc(value).tz(systemTimezone).format(format);
};

const formatUtcTimePartInSystemTimezone = (value, format = "HH:mm:ss") => {
  if (!value) return "";

  if (isUtcDateTimeString(value)) {
    return dayjs.utc(value).tz(systemTimezone).format(format);
  }

  const match = String(value).match(/T(\d{2}:\d{2}:\d{2})/);
  if (!match) return "";

  return dayjs.utc(`2000-01-01T${match[1]}Z`).tz(systemTimezone).format(format);
};

const formatSlotTimeInSystemTimezone = (
  slot,
  selectedDateValue,
  targetTimezone,
) => {
  const start = getUtcSlotStart(slot, selectedDateValue);

  if (!start || !start.isValid()) return slot;

  return start.tz(targetTimezone || systemTimezone).format("hh:mm A");
};

const formatUtcSlotSummaryInSystemTimezone = (
  slot,
  selectedDateValue,
  duration,
) => {
  if (!slot) {
    return { date: "", time: "" };
  }

  const start = getUtcSlotStart(slot, selectedDateValue);

  if (!start || !start.isValid()) {
    return {
      date: selectedDateValue ? selectedDateValue.format("MMMM D, YYYY") : "",
      time: formatTimeSlotRange(slot, duration),
    };
  }

  const localStart = start.tz(systemTimezone);
  const localEnd = localStart.add(duration || 30, "minute");

  return {
    date: localStart.format("MMMM D, YYYY"),
    time: `${localStart.format("hh:mm A")} - ${localEnd.format("hh:mm A")}`,
  };
};

const getUtcPayloadDateTime = (slot, selectedDateValue) => {
  const utcSlotStart = getUtcSlotStart(slot, selectedDateValue);

  if (utcSlotStart && utcSlotStart.isValid()) {
    return {
      date: utcSlotStart.utc().format("YYYY-MM-DD"),
      time: utcSlotStart.utc().format("HH:mm:ss"),
      dateTime: utcSlotStart.utc(),
    };
  }

  if (!slot || !selectedDateValue) {
    return { date: "", time: "", dateTime: null };
  }

  const normalizedSlot = slot.length === 5 ? `${slot}:00` : slot;
  const localDate = selectedDateValue.format("YYYY-MM-DD");
  const localDateTime = dayjs.tz(
    `${localDate} ${normalizedSlot}`,
    "YYYY-MM-DD HH:mm:ss",
    systemTimezone,
  );

  if (!localDateTime.isValid()) {
    return { date: localDate, time: normalizedSlot, dateTime: null };
  }

  const utcDateTime = localDateTime.utc();

  return {
    date: utcDateTime.format("YYYY-MM-DD"),
    time: utcDateTime.format("HH:mm:ss"),
    dateTime: utcDateTime,
  };
};

const ScheduleMeeting = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const { id, customUrl } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isRescheduleMode =
    searchParams.get("mode") === "reschedule" || Boolean(id);

  const [existingMeeting, setExistingMeeting] = useState(null);
  const [config, setConfig] = useState(null);
  const [loadingMeeting, setLoadingMeeting] = useState(true);
  const [errorStatus, setErrorStatus] = useState(false);

  const [allMeetingTypes, setAllMeetingTypes] = useState([]);
  const [activeMeetingTypes, setActiveMeetingTypes] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const defaultDate =
    isRescheduleMode && existingMeeting
      ? dayjs.utc(existingMeeting.dueDate).tz(systemTimezone)
      : null;
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [typeAvailabilityWarning, setTypeAvailabilityWarning] = useState("");
  const [calendarViewDate, setCalendarViewDate] = useState(
    dayjs().startOf("day"),
  );
  const [slotsCache, setSlotsCache] = useState({});
  const [fetchingDates, setFetchingDates] = useState(new Set());

  const [isSuccess, setIsSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [successBookingId, setSuccessBookingId] = useState(null);
  const [form] = Form.useForm();
  const selectedSlot = Form.useWatch("timeSlot", form);
  const resolvedTimezone =
    config?.timezone || existingMeeting?.timezone || fallbackTimezone;

  const selectedMeetingTypeObj = useMemo(
    () =>
      activeMeetingTypes.find((type) => type.meetingTypeId === selectedType) ||
      null,
    [activeMeetingTypes, selectedType],
  );

  const selectedSlotSummary = useMemo(
    () =>
      formatUtcSlotSummaryInSystemTimezone(
        selectedSlot,
        selectedDate,
        selectedMeetingTypeObj?.durationMinutes || 30,
      ),
    [selectedSlot, selectedDate, selectedMeetingTypeObj],
  );

  useEffect(() => {
    setIsSuccess(false);
    setSummary(null);
    setSuccessBookingId(null);
    setErrorStatus(false);
    setLoadingMeeting(true);
    setSubmitting(false);
    setAvailableSlots([]);

    if (isRescheduleMode && id) {
      getMethod(`${publicBooking}/manage/${id}`)
        .then((res) => {
          setExistingMeeting(res.data);
          const mappedDate = dayjs.utc(res.data.dueDate).tz(systemTimezone);
          setSelectedDate(mappedDate);
          setCalendarViewDate(mappedDate);
          setSelectedType(res.data.meetingTypeId);

          if (res.data.customUrl) {
            return getMethod(`${publicBooking}/${res.data.customUrl}`);
          }
          return null;
        })
        .then((configRes) => {
          if (
            configRes &&
            configRes.data &&
            configRes.data.availableMeetingTypes
          ) {
            setAllMeetingTypes(configRes.data.availableMeetingTypes);
            setActiveMeetingTypes(configRes.data.availableMeetingTypes);
          }
          setLoadingMeeting(false);
        })
        .catch((err) => {
          console.error("Failed to load booking details:", err);
          setErrorStatus(true);
          setLoadingMeeting(false);
        });
    } else {
      getMethod(`${publicBooking}/${customUrl}`)
        .then((res) => {
          setConfig(res.data);
          const meetingTypes = res.data?.availableMeetingTypes || [];
          setAllMeetingTypes(meetingTypes);
          setActiveMeetingTypes(meetingTypes);
          if (meetingTypes.length === 1) {
            setSelectedType(meetingTypes[0].meetingTypeId);
          } else {
            setSelectedType(null);
          }
          setLoadingMeeting(false);
        })
        .catch((err) => {
          console.error("Link fetch error:", err);
          setErrorStatus(true);
          setLoadingMeeting(false);
        });
    }
  }, [customUrl, id, isRescheduleMode]);

  useEffect(() => {
    if (!isRescheduleMode || !existingMeeting) return;

    form.setFieldsValue({
      name: existingMeeting.guestName,
      email: existingMeeting.guestEmail,
      date: dayjs.utc(existingMeeting.dueDate).tz(systemTimezone),
      timeSlot: formatUtcTimePartInSystemTimezone(
        existingMeeting.dueDate,
        "HH:mm:ss",
      ),
      reminder: existingMeeting.reminderMinutes
        ? String(existingMeeting.reminderMinutes)
        : "30",
    });
  }, [existingMeeting, form, isRescheduleMode, resolvedTimezone]);

  useEffect(() => {
    if (!selectedDate) {
      setActiveMeetingTypes(allMeetingTypes);
      setTypeAvailabilityWarning("");
      return;
    }

    const bookingUrl = isRescheduleMode
      ? existingMeeting?.customUrl
      : customUrl;
    if (!bookingUrl || allMeetingTypes.length === 0) return;

    const dateStr = selectedDate.format("YYYY-MM-DD");

    Promise.all(
      allMeetingTypes.map((type) =>
        getMethod(
          `${publicBooking}/${bookingUrl}/availability?date=${dateStr}&typeId=${type.meetingTypeId}`,
        )
          .then((res) => ({
            type,
            slots: (res.data || []).map((item) => item.time || item),
          }))
          .catch(() => ({
            type,
            slots: [],
          })),
      ),
    ).then((results) => {
      const availableTypes = results
        .filter(({ slots }) => slots.length > 0)
        .map(({ type }) => type);
      setActiveMeetingTypes(allMeetingTypes);

      if (availableTypes.length === 0) {
        if (selectedType) {
          setTypeAvailabilityWarning(
            "The selected meeting type is not available for this day. Please choose another meeting type.",
          );
        } else {
          setTypeAvailabilityWarning("");
        }
        setAvailableSlots([]);
        form.setFieldValue("timeSlot", null);
        return;
      }

      const selectedTypeStillAvailable = availableTypes.some(
        (type) => type.meetingTypeId === selectedType,
      );

      if (!selectedTypeStillAvailable) {
        setAvailableSlots([]);
        form.setFieldValue("timeSlot", null);
        setTypeAvailabilityWarning(
          "The selected meeting type is not available for this day. Please choose another meeting type.",
        );
        return;
      }

      setTypeAvailabilityWarning("");
    });
  }, [
    allMeetingTypes,
    customUrl,
    existingMeeting?.customUrl,
    form,
    isRescheduleMode,
    selectedDate,
    selectedType,
  ]);

  useEffect(() => {
    if (!selectedType) {
      setSlotsCache({});
      return;
    }
    const bookingUrl = isRescheduleMode
      ? existingMeeting?.customUrl
      : customUrl;
    if (!bookingUrl) return;

    const startOfMonth = calendarViewDate.startOf("month");
    const endOfMonth = calendarViewDate.endOf("month");
    const today = dayjs().startOf("day");

    const datesToFetch = [];
    let current = startOfMonth;
    while (current.isBefore(endOfMonth) || current.isSame(endOfMonth)) {
      if (!current.isBefore(today, "day")) {
        const dateStr = current.format("YYYY-MM-DD");
        const cacheKey = `${selectedType}_${dateStr}`;
        if (
          slotsCache[cacheKey] === undefined &&
          !fetchingDates.has(cacheKey)
        ) {
          datesToFetch.push(dateStr);
        }
      }
      current = current.add(1, "day");
    }

    if (datesToFetch.length === 0) return;

    setFetchingDates((prev) => {
      const next = new Set(prev);
      datesToFetch.forEach((d) => next.add(`${selectedType}_${d}`));
      return next;
    });

    Promise.all(
      datesToFetch.map((dateStr) =>
        getMethod(
          `${publicBooking}/${bookingUrl}/availability?date=${dateStr}&typeId=${selectedType}`,
        )
          .then((res) => ({
            dateStr,
            slotsCount: (res.data || []).length,
          }))
          .catch(() => ({
            dateStr,
            slotsCount: 0,
          })),
      ),
    ).then((results) => {
      setSlotsCache((prev) => {
        const next = { ...prev };
        results.forEach(({ dateStr, slotsCount }) => {
          next[`${selectedType}_${dateStr}`] = slotsCount;
        });
        return next;
      });
      setFetchingDates((prev) => {
        const next = new Set(prev);
        datesToFetch.forEach((d) => next.delete(`${selectedType}_${d}`));
        return next;
      });
    });
  }, [
    calendarViewDate,
    selectedType,
    customUrl,
    existingMeeting?.customUrl,
    isRescheduleMode,
  ]);

  useEffect(() => {
    if (!selectedDate || !selectedType) return;

    const bookingUrl = isRescheduleMode
      ? existingMeeting?.customUrl
      : customUrl;
    if (!bookingUrl) return;

    setLoadingSlots(true);
    setAvailableSlots([]);
    form.setFieldValue("timeSlot", null);

    const dateStr = selectedDate.format("YYYY-MM-DD");
    getMethod(
      `${publicBooking}/${bookingUrl}/availability?date=${dateStr}&typeId=${selectedType}`,
    )
      .then((res) => {
        const slots = (res.data || []).map((item) => item.time || item);
        setAvailableSlots(slots);
        setLoadingSlots(false);
      })
      .catch((err) => {
        console.error("Failed to load slots:", err);
        setLoadingSlots(false);
      });
  }, [
    customUrl,
    existingMeeting?.customUrl,
    form,
    isRescheduleMode,
    selectedDate,
    selectedType,
  ]);

  const headingTitle = isRescheduleMode
    ? "Reschedule Meeting"
    : "Schedule a Meeting";
  const headingDescription = isRescheduleMode
    ? "Pick a new date and time for this meeting."
    : "Select a date and time that works for you";
  const submitLabel = isRescheduleMode
    ? "Reschedule Meeting"
    : "Schedule Meeting";
  const successTitle = isRescheduleMode
    ? "Meeting Rescheduled Successfully"
    : "Meeting Scheduled Successfully";
  const successDescription = isRescheduleMode
    ? "Your meeting has been updated in the BeyondLeadz calendar."
    : "Your meeting has been added to the BeyondLeadz calendar.";

  const resetSchedulingState = () => {
    form.resetFields(["date", "timeSlot", "name", "email", "notes"]);
    setSelectedType(null);
    setTypeAvailabilityWarning("");
    setSelectedDate(null);
    setCalendarViewDate(dayjs().startOf("day"));
    setAvailableSlots([]);
  };

  const exitRescheduleFlow = () => {
    resetSchedulingState();
    setIsSuccess(false);
    setSummary(null);
    setSuccessBookingId(null);

    if (existingMeeting?.customUrl) {
      navigate(`/schedule-meeting/${existingMeeting.customUrl}`);
    }
  };

  const onFinish = (values) => {
    const meetingTitle =
      selectedMeetingTypeObj?.meetingName ||
      selectedMeetingTypeObj?.typeName ||
      existingMeeting?.title ||
      "Meeting";

    if (isRescheduleMode) {
      setSubmitting(true);
      const utcPayload = getUtcPayloadDateTime(values?.timeSlot, values?.date);
      // Construct PUT payload
      const payload = {
        bookingUuid: id,
        title: meetingTitle,
        date: utcPayload.date,
        time: utcPayload.time,
        guestName: values?.name || "",
        guestEmail: values?.email || "",
        reminderMinutes: values?.reminder || "30",
        meetingTypeId: existingMeeting?.meetingTypeId,
        calendarEventTypeId: existingMeeting?.calendarEventTypeId,
        addToCalendar: true,
      };

      putMethod(`${publicBooking}/update`, id, payload)
        .then((res) => {
          setSubmitting(false);
          const durationMins = selectedMeetingTypeObj?.durationMinutes || 30;
          const localSummary = formatUtcSlotSummaryInSystemTimezone(
            payload.time,
            values?.date ? dayjs(values.date) : null,
            durationMins,
          );
          const scheduled = {
            title: meetingTitle,
            date: localSummary.date,
            time: localSummary.time,
            duration: `${durationMins} minutes`,
            taskType: selectedMeetingTypeObj?.meetingName || "Meeting",
            name: values?.name || "",
            email: values?.email || "",
          };
          setSummary(scheduled);
          setSuccessBookingId(id);
          resetSchedulingState();
          setIsSuccess(true);
        })
        .catch((err) => {
          setSubmitting(false);
        });
    } else {
      if (!selectedDate || !selectedSlot || !selectedType || !customUrl) return;

      setSubmitting(true);
      const utcPayload = getUtcPayloadDateTime(selectedSlot, selectedDate);
      const payload = {
        calendarEventTypeId: 1,
        meetingTypeId: selectedType,
        title: meetingTitle,
        date: utcPayload.date,
        time: utcPayload.time,
        guestName: values?.name || "",
        guestEmail: values?.email || "",
        comments: values?.notes || "",
        reminderMinutes: values?.reminder || "30",
        addToCalendar: true,
      };

      postMethod(`${publicBooking}/${customUrl}/book`, payload)
        .then((res) => {
          setSubmitting(false);
          const durationMins = selectedMeetingTypeObj?.durationMinutes || 30;
          const localSummary = formatUtcSlotSummaryInSystemTimezone(
            selectedSlot,
            selectedDate,
            durationMins,
          );
          const scheduled = {
            title: meetingTitle,
            date: localSummary.date,
            time: localSummary.time,
            duration: `${durationMins} minutes`,
            taskType: selectedMeetingTypeObj?.meetingName || "Meeting",
            name: values?.name || "",
            email: values?.email || "",
            conferenceLink: res?.data?.conferenceLink,
          };
          setSummary(scheduled);
          setSuccessBookingId(
            res?.data?.bookingUuid || res?.data?.id || res?.data?.uuid || null,
          );
          resetSchedulingState();
          setIsSuccess(true);
        })
        .catch((err) => {
          setSubmitting(false);
        });
    }
  };

  const disabledDate = (current) => {
    if (!current) return false;
    const today = dayjs().startOf("day");
    if (current.isBefore(today, "day")) return true;

    if (selectedType) {
      const dateStr = current.format("YYYY-MM-DD");
      const cacheKey = `${selectedType}_${dateStr}`;
      const cached = slotsCache[cacheKey];
      if (cached !== undefined) {
        if (typeof cached === "number") {
          return cached === 0;
        }
        if (Array.isArray(cached)) {
          return cached.length === 0;
        }
      }
    }

    return false;
  };

  const dateCellRender = (value) => {
    const isCurrentMonth = value.isSame(calendarViewDate, "month");
    const isDisabled = disabledDate(value);
    const isSelected = selectedDate && value.isSame(selectedDate, "day");

    const textClass =
      isDisabled || !isCurrentMonth
        ? "tw-text-slate-300 tw-font-normal"
        : isSelected
          ? "tw-text-white tw-font-bold"
          : "tw-text-slate-700 tw-font-semibold hover:tw-text-theme";

    return (
      <div
        className={`tw-mx-auto tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-text-[14px] tw-transition-all tw-duration-200 ${
          isDisabled
            ? "tw-cursor-not-allowed tw-bg-slate-50/50 tw-opacity-40"
            : "tw-cursor-pointer"
        } ${
          isSelected && !isDisabled
            ? "tw-bg-theme"
            : !isDisabled && isCurrentMonth
              ? "hover:tw-bg-slate-100"
              : ""
        }`}
      >
        <span className={textClass}>{value.date()}</span>
      </div>
    );
  };

  if (loadingMeeting) {
    return (
      <div className="tw-py-40 tw-text-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="schedule-meeting-container tw-min-h-screen tw-bg-[#F8FAFC] tw-py-10 sm:tw-py-16 tw-px-4 sm:tw-px-8 tw-flex tw-items-center tw-justify-center tw-font-inter">
      <div className="tw-w-full tw-max-w-[1080px] tw-bg-white tw-rounded-3xl tw-border tw-border-solid tw-border-slate-200/70 tw-shadow-[0_24px_64px_rgba(15,23,42,0.07)] tw-overflow-hidden">
        {isSuccess ? (
          <div className="tw-p-8 sm:tw-p-12">
            <MeetingSuccessState
              title={successTitle}
              description={successDescription}
              summaryTitle="Meeting Summary"
              summaryRows={[
                { label: "Title", value: summary?.title },
                { label: "Date", value: summary?.date },
                { label: "Time", value: summary?.time },
                { label: "Duration", value: summary?.duration },
                { label: "Type", value: summary?.taskType },
                { label: "Name", value: summary?.name },
                { label: "Email", value: summary?.email },
                ...(summary?.conferenceLink
                  ? [
                      {
                        label: "Meeting Link",
                        value: (
                          <a
                            href={summary.conferenceLink}
                            target="_blank"
                            rel="noreferrer"
                            className="tw-text-theme tw-underline tw-font-semibold"
                          >
                            Join Meeting
                          </a>
                        ),
                      },
                    ]
                  : []),
              ]}
              actions={[
                {
                  icon: <RefreshCw size={14} />,
                  label: "Schedule Another",
                  onClick: () => {
                    if (isRescheduleMode && existingMeeting?.customUrl) {
                      exitRescheduleFlow();
                      return;
                    }
                    setIsSuccess(false);
                    setSuccessBookingId(null);
                    resetSchedulingState();
                  },
                },
                {
                  icon: <Pencil size={14} />,
                  label: "Reschedule",
                  onClick: () => {
                    const bookingIdToReschedule = successBookingId || id;
                    setIsSuccess(false);
                    if (bookingIdToReschedule) {
                      navigate(`/reschedule-meeting/${bookingIdToReschedule}`);
                      return;
                    }
                  },
                },
              ]}
            />
          </div>
        ) : (
          <div className="tw-flex tw-flex-col lg:tw-flex-row">
            {/* Left Sidebar - Calendly Host & Event Summary */}
            <div className="tw-w-full lg:tw-w-[340px] tw-bg-[#FAFAFC] tw-border-b tw-border-l-0 tw-border-t-0 lg:tw-border-b-0 lg:tw-border-r tw-border-solid tw-border-slate-200/80 tw-p-7 sm:tw-p-9 tw-flex tw-flex-col tw-justify-between">
              <div>
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-8">
                  <NavLink to={isAuthenticated ? "/dashboard-view" : "/"} className="hover:tw-opacity-80 tw-transition-opacity">
                    <img
                      src={LogoSvg}
                      alt="BeyondLeadz"
                      style={{ height: "22px", width: "auto" }}
                      className="tw-object-contain tw-cursor-pointer"
                    />
                  </NavLink>
                  <div className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-bg-indigo-50 tw-text-theme tw-font-bold tw-text-sm">
                    {config?.userName?.[0] || "B"}
                  </div>
                </div>

                <div className="tw-text-xs tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-mb-1.5">
                  {config?.userName || "BeyondLeadz Team"}
                </div>

                <h1 className="tw-text-xl sm:tw-text-2xl tw-font-extrabold tw-text-slate-800 tw-tracking-tight tw-mb-5">
                  {selectedMeetingTypeObj?.meetingName || headingTitle}
                </h1>

                <div className="tw-flex tw-flex-col tw-gap-3.5 tw-mb-8">
                  <div className="tw-flex tw-items-center tw-gap-2.5 tw-text-sm tw-font-semibold tw-text-slate-600">
                    <Clock
                      size={16}
                      className="tw-text-slate-400 tw-flex-shrink-0"
                    />
                    <span>
                      {selectedMeetingTypeObj?.durationMinutes || 30} mins
                    </span>
                  </div>

                  <div className="tw-flex tw-items-center tw-gap-2.5 tw-text-sm tw-font-semibold tw-text-slate-600">
                    <Video
                      size={16}
                      className="tw-text-slate-400 tw-flex-shrink-0"
                    />
                    <span>Google Meet</span>
                  </div>

                  {selectedDate && selectedSlot ? (
                    <div className="tw-mt-3 tw-p-4 tw-rounded-2xl tw-bg-indigo-50/80 tw-border tw-border-solid tw-border-indigo-100 tw-text-theme">
                      <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-extrabold tw-uppercase tw-tracking-wide tw-mb-1">
                        <CheckCircle2 size={14} /> Selected Slot
                      </div>
                      <div className="tw-text-sm tw-font-bold tw-text-slate-800">
                        {selectedSlotSummary.time}
                      </div>
                      <div className="tw-text-xs tw-font-semibold tw-text-slate-600 tw-mt-0.5">
                        {selectedSlotSummary.date}
                      </div>
                    </div>
                  ) : null}
                </div>

                {selectedMeetingTypeObj?.description ? (
                  <p className="tw-text-xs tw-text-slate-500 tw-leading-relaxed tw-mb-8">
                    {selectedMeetingTypeObj.description}
                  </p>
                ) : null}

                {isRescheduleMode && existingMeeting ? (
                  <div className="tw-mb-8 tw-bg-amber-50/80 tw-border tw-border-solid tw-border-amber-200/80 tw-rounded-2xl tw-p-4">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                      <div className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-amber-500 tw-animate-pulse" />
                      <span className="tw-text-xs tw-font-bold tw-text-amber-900">
                        Currently Scheduled
                      </span>
                    </div>
                    <div className="tw-text-xs tw-font-bold tw-text-slate-800">
                      {existingMeeting.meetingName}
                    </div>
                    <div className="tw-text-xs tw-text-slate-600 tw-mt-0.5">
                      {formatUtcDateTimeInSystemTimezone(
                        existingMeeting.dueDate,
                        "MMMM D, YYYY",
                      )}{" "}
                      •{" "}
                      {formatTimeSlotRange(
                        formatUtcTimePartInSystemTimezone(
                          existingMeeting.dueDate,
                          "HH:mm:ss",
                        ),
                        existingMeeting.durationMinutes || 30,
                      )}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Timezone Footer */}
              <div className="tw-pt-6 tw-border-t tw-border-b-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-200/60 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-text-slate-500">
                <Globe size={14} className="tw-text-slate-400" />
                <span>Timezone: {resolvedTimezone}</span>
              </div>
            </div>

            {/* Right Panel - Calendly Booking Form */}
            <div className="tw-flex-1 tw-min-w-0 tw-p-7 sm:tw-p-10">
              {/* Meeting Type Selector Pill Bar */}
              {activeMeetingTypes.length > 1 ? (
                <div className="tw-mb-8">
                  <div className="tw-text-xs tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-mb-3">
                    Select Meeting Type
                  </div>
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2.5 tw-max-h-[160px] tw-overflow-y-auto tw-pr-1">
                    {activeMeetingTypes.map((type) => {
                      const isSelected = selectedType === type.meetingTypeId;

                      return (
                        <button
                          key={type.meetingTypeId}
                          type="button"
                          onClick={() => {
                            setSelectedType(type.meetingTypeId);
                            setTypeAvailabilityWarning("");
                            form.setFieldValue("timeSlot", null);
                          }}
                          className={`tw-cursor-pointer tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-full tw-text-xs tw-font-bold tw-transition-all tw-whitespace-nowrap tw-border tw-border-solid ${
                            isSelected
                              ? "tw-bg-theme tw-text-white tw-border-theme tw-shadow-sm"
                              : "tw-bg-slate-50 tw-text-slate-600 tw-border-slate-200/80 hover:tw-bg-slate-100"
                          }`}
                        >
                          <Clock size={12} className="tw-shrink-0" />
                          <span className="tw-truncate tw-max-w-[220px]">
                            {type.meetingName}
                          </span>
                          <span className="tw-opacity-80 tw-shrink-0">
                            ({type.durationMinutes}m)
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <div className="tw-mb-6 tw-flex tw-items-center tw-gap-2.5 tw-bg-indigo-50/70 tw-text-indigo-900 tw-px-4 tw-py-3 tw-rounded-xl tw-border tw-border-solid tw-border-indigo-100 tw-text-xs tw-font-semibold">
                <Info size={16} className="tw-text-theme tw-shrink-0" />
                <span>
                  Note: Availability is shown after selecting a meeting type and
                  date.
                </span>
              </div>

              {typeAvailabilityWarning ? (
                <Alert
                  type="warning"
                  showIcon
                  message={typeAvailabilityWarning}
                  className="tw-mb-8 tw-rounded-2xl"
                />
              ) : null}
              <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                onFinish={onFinish}
                initialValues={{
                  name:
                    isRescheduleMode && existingMeeting
                      ? existingMeeting.guestName
                      : undefined,
                  email:
                    isRescheduleMode && existingMeeting
                      ? existingMeeting.guestEmail
                      : undefined,
                  notes: undefined,
                  date: defaultDate || undefined,
                  timeSlot:
                    defaultDate && existingMeeting
                      ? formatUtcTimePartInSystemTimezone(
                          existingMeeting.dueDate,
                          "HH:mm:ss",
                        )
                      : undefined,
                  reminder:
                    isRescheduleMode &&
                    existingMeeting &&
                    existingMeeting.reminderMinutes
                      ? String(existingMeeting.reminderMinutes)
                      : "30",
                }}
              >                {/* 2-Column Split: Calendar (Fixed 360px) & Time Slots */}
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-[360px_1fr] tw-gap-8 lg:tw-gap-10 tw-mb-10">
                  {/* Calendar Column (Left Side) - Strictly fixed 360px width */}
                  <div className="tw-w-[360px] tw-shrink-0">
                    <div className="tw-text-sm tw-font-bold tw-text-slate-800 tw-mb-4">
                      Select a Date
                    </div>
                    <Form.Item
                      name="date"
                      className="tw-mb-0"
                      rules={[
                        { required: true, message: "Please select a date" },
                      ]}
                    >
                      <Calendar
                        fullscreen={false}
                        disabledDate={disabledDate}
                        value={selectedDate || calendarViewDate}
                        onSelect={(value) => {
                          if (!selectedType) {
                            setTypeAvailabilityWarning(
                              "Please select a meeting type first to view available dates and time slots.",
                            );
                            return;
                          }
                          if (disabledDate(value)) return;
                          setSelectedDate(value);
                          setCalendarViewDate(value);
                          setTypeAvailabilityWarning("");
                          form.setFieldValue("date", value);
                          form.setFieldValue("timeSlot", null);
                        }}
                        fullCellRender={dateCellRender}
                        headerRender={({ value, onChange }) => (
                          <div className="tw-flex tw-items-center tw-justify-between tw-border-solid tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-b tw-border-slate-100 tw-bg-white tw-px-4 tw-py-3.5">
                            <ChevronLeft
                              className="tw-p-1.5 tw-rounded-full hover:tw-bg-slate-100 tw-transition-all tw-cursor-pointer tw-text-slate-600"
                              size={28}
                              onClick={() => {
                                const nextValue = value.subtract(1, "month");
                                setCalendarViewDate(nextValue);
                                onChange(nextValue);
                              }}
                            />
                            <span className="tw-text-sm tw-font-bold tw-text-slate-800">
                              {value.format("MMMM YYYY")}
                            </span>
                            <ChevronRight
                              className="tw-p-1.5 tw-rounded-full hover:tw-bg-slate-100 tw-transition-all tw-cursor-pointer tw-text-slate-600"
                              size={28}
                              onClick={() => {
                                const nextValue = value.add(1, "month");
                                setCalendarViewDate(nextValue);
                                onChange(nextValue);
                              }}
                            />
                          </div>
                        )}
                        className="schedule-meeting-calendar tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/80"
                      />
                    </Form.Item>
                  </div>

                  {/* Time Slots Column - Rendered ONLY when both date AND meeting type are selected */}
                  {selectedDate && selectedType ? (
                    <div className="tw-w-full">
                      <div className="tw-text-sm tw-font-bold tw-text-slate-800 tw-mb-4">
                        {selectedDate.format("dddd, MMM D")}
                      </div>

                      <div>
                        {loadingSlots ? (
                          <div className="tw-py-12 tw-text-center">
                            <Spin />
                          </div>
                        ) : availableSlots.length === 0 ? (
                          <div className="tw-rounded-2xl tw-bg-red-50/80 tw-text-red-600 tw-p-4 tw-text-xs tw-font-semibold">
                            No available slots for this date.
                          </div>
                        ) : (
                          <Form.Item
                            name="timeSlot"
                            rules={[
                              {
                                required: true,
                                message: "Please select a time slot",
                              },
                            ]}
                            className="tw-mb-0"
                          >
                            <div className="schedule-time-slots-scroll tw-flex tw-flex-col tw-gap-3 tw-max-h-[380px] tw-overflow-y-auto tw-pr-3.5 tw-py-1">
                              {availableSlots.map((slot) => {
                                const isSelected = selectedSlot === slot;
                                const formattedSlot =
                                  formatSlotTimeInSystemTimezone(
                                    slot,
                                    selectedDate,
                                    systemTimezone,
                                  );

                                if (isSelected) {
                                  return (
                                    <div
                                      key={slot}
                                      className="tw-flex tw-items-center tw-gap-2.5 tw-w-full"
                                    >
                                      <div className="tw-flex-1 !tw-h-[52px] !tw-py-3.5 tw-flex tw-items-center tw-justify-center !tw-rounded-xl !tw-bg-slate-100 !tw-border !tw-border-solid !tw-border-slate-300/80 !tw-text-slate-700 tw-text-[15px] tw-font-bold">
                                        {formattedSlot}
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const detailsEl =
                                            document.getElementById(
                                              "attendee-details-section",
                                            );
                                          if (detailsEl) {
                                            detailsEl.scrollIntoView({
                                              behavior: "smooth",
                                            });
                                          }
                                        }}
                                        className="schedule-next-btn tw-flex-1 !tw-h-[52px] !tw-py-3.5 tw-flex tw-items-center tw-justify-center !tw-rounded-xl !tw-bg-theme hover:!tw-bg-[#4B35E0] !tw-text-white tw-text-[15px] tw-font-bold !tw-border-0 !tw-outline-none hover:tw-scale-[1.02] tw-transition-all tw-cursor-pointer"
                                      >
                                        Next
                                      </button>
                                    </div>
                                  );
                                }

                                return (
                                  <button
                                    key={slot}
                                    type="button"
                                    onClick={() =>
                                      form.setFieldValue("timeSlot", slot)
                                    }
                                    className="tw-w-full !tw-h-[52px] !tw-py-3.5 tw-flex tw-items-center tw-justify-center !tw-rounded-xl tw-border tw-border-solid tw-border-slate-200/90 tw-bg-white tw-text-slate-800 tw-text-[15px] tw-font-bold hover:tw-border-[#5D44FF] hover:tw-text-[#5D44FF] hover:tw-bg-indigo-50/20 tw-transition-all tw-cursor-pointer"
                                  >
                                    <span>{formattedSlot}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </Form.Item>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* Attendee Details Section */}
                <div
                  id="attendee-details-section"
                  className="tw-pt-8 tw-mt-10 tw-border-t tw-border-b-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100"
                >
                  <h3 className="tw-text-base tw-font-bold tw-text-slate-800 tw-mb-6">
                    Enter Details
                  </h3>

                  <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-5 tw-mb-5">
                    <Form.Item
                      name="name"
                      label={
                        <span className="tw-text-[13px] tw-font-bold tw-text-slate-700">
                          Your Name <span className="tw-text-rose-500">*</span>
                        </span>
                      }
                      rules={[
                        { required: true, message: "Please enter your name" },
                      ]}
                      className="tw-mb-0"
                    >
                      <Input
                        placeholder="John Doe"
                        className="!tw-h-[46px] !tw-rounded-xl !tw-border-slate-200 hover:!tw-border-slate-300 focus:!tw-border-[#5D44FF] tw-text-sm"
                      />
                    </Form.Item>

                    <Form.Item
                      name="email"
                      label={
                        <span className="tw-text-[13px] tw-font-bold tw-text-slate-700">
                          Your Email <span className="tw-text-rose-500">*</span>
                        </span>
                      }
                      rules={[
                        { required: true, message: "Please enter your email" },
                        {
                          type: "email",
                          message: "Please enter a valid email",
                        },
                      ]}
                      className="tw-mb-0"
                    >
                      <Input
                        placeholder="john@example.com"
                        className="!tw-h-[46px] !tw-rounded-xl !tw-border-slate-200 hover:!tw-border-slate-300 focus:!tw-border-[#5D44FF] tw-text-sm"
                      />
                    </Form.Item>
                  </div>

                  <Form.Item
                    name="notes"
                    label={
                      <span className="tw-text-[13px] tw-font-bold tw-text-slate-700">
                        Additional Notes{" "}
                        <span className="tw-text-slate-400 tw-font-normal">
                          (optional)
                        </span>
                      </span>
                    }
                    className="tw-mb-5"
                  >
                    <TextArea
                      rows={3}
                      placeholder="Please share anything that will help prepare for our meeting..."
                      className="!tw-rounded-xl !tw-border-slate-200 hover:!tw-border-slate-300 focus:!tw-border-[#5D44FF] tw-text-sm tw-py-3"
                    />
                  </Form.Item>

                  <div className="tw-w-full sm:tw-w-[280px] tw-mb-8">
                    <Form.Item
                      name="reminder"
                      label={
                        <span className="tw-text-[13px] tw-font-bold tw-text-slate-700">
                          Email Reminder
                        </span>
                      }
                      className="tw-mb-0"
                    >
                      <Select
                        className="schedule-reminder-select !tw-h-[46px] !tw-rounded-xl"
                        popupClassName="schedule-reminder-popup"
                        options={[
                          { label: "15 minutes before", value: "15" },
                          { label: "30 minutes before", value: "30" },
                          { label: "60 minutes before", value: "60" },
                        ]}
                      />
                    </Form.Item>
                  </div>

                  {/* Form Action Footer */}
                  <div className="tw-pt-6 tw-border-t tw-border-b-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100 tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-end tw-gap-3">
                    {isRescheduleMode ? (
                      <Button
                        onClick={exitRescheduleFlow}
                        className="!tw-h-[48px] !tw-rounded-xl !tw-border-slate-200 !tw-bg-white !tw-px-6 !tw-text-xs !tw-font-bold !tw-text-slate-600 hover:!tw-border-slate-400 tw-transition-all"
                      >
                        Cancel
                      </Button>
                    ) : null}

                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={submitting}
                      disabled={
                        !selectedType || !selectedDate || !selectedSlot
                      }
                      className="theme-btn !tw-h-[48px] !tw-rounded-xl tw-font-bold tw-text-xs sm:tw-text-sm !tw-px-8 tw-w-full sm:tw-w-auto hover:tw-scale-[1.01] tw-transition-all !tw-bg-[#5D44FF] hover:!tw-bg-[#4F36F2] !tw-shadow-[0_4px_14px_rgba(93,68,255,0.25)]"
                    >
                      {submitLabel}
                    </Button>
                  </div>
                </div>
              </Form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleMeeting;
